import { useState, useRef } from "react";
import { Upload, FileText, X, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";

interface SimplePaymentUploaderProps {
  onUploadComplete: (results: { uploadURL: string; filename: string }[]) => void;
  onUpload?: (files: File[]) => Promise<{ uploadURL: string; filename: string }[]>;
  onSendForApproval?: (files: File[]) => Promise<void>;
  existingPayment?: any;
}

export function SimplePaymentUploader({ onUploadComplete, onUpload, onSendForApproval, existingPayment }: SimplePaymentUploaderProps) {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      setUploadSuccess(false);

      const validNewFiles: File[] = [];
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];

      // Process new files for type and size validity first
      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        if (!allowedTypes.includes(file.type)) {
          toast({
            title: "Invalid File Type",
            description: `${file.name}: Please upload PDF, JPEG, or PNG files only.`,
            variant: "destructive",
          });
          continue;
        }

        if (file.size > 5242880) {
          toast({
            title: "File Too Large",
            description: `${file.name}: File size must be less than 5MB.`,
            variant: "destructive",
          });
          continue;
        }

        validNewFiles.push(file);
      }

      if (validNewFiles.length === 0) return;

      // Create proposed state to check constraints
      const proposedFiles = [...selectedFiles, ...validNewFiles];
      const hasPdf = proposedFiles.some(f => f.type === 'application/pdf');

      // Constraint Check: 1 PDF only OR up to 5 Images
      if (hasPdf) {
        if (proposedFiles.length > 1) {
          toast({
            title: "Invalid Selection",
            description: "You can upload either 1 PDF file OR up to 5 images.",
            variant: "destructive",
          });
          if (fileInputRef.current) fileInputRef.current.value = '';
          return;
        }
      } else {
        if (proposedFiles.length > 5) {
          toast({
            title: "Too Many Files",
            description: "You can upload a maximum of 5 images.",
            variant: "destructive",
          });
          if (fileInputRef.current) fileInputRef.current.value = '';
          return;
        }
      }

      // If checks pass, update state
      setSelectedFiles(proposedFiles);
      toast({
        title: "Files Selected",
        description: `${proposedFiles.length} file(s) ready to upload`,
      });

      // Reset input to allow selecting the same file again if needed
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const clearSelection = () => {
    setSelectedFiles([]);
    setUploadProgress(0);
    setUploadSuccess(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Don't show upload area if payment already exists - redirect will be handled by parent
  if (existingPayment) {
    return null;
  }

  return (
    <div className="space-y-4">
      {/* File Selection */}
      <div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={handleFileSelect}
          multiple
          className="hidden"
        />

        {selectedFiles.length === 0 ? (
          <Button
            onClick={() => fileInputRef.current?.click()}
            className="w-full p-12 border-2 border-dashed border-emerald-300 rounded-xl bg-emerald-50 hover:bg-emerald-100 transition-colors text-center min-h-[250px] flex items-center justify-center"
            disabled={uploading}
          >
            <div className="flex flex-col items-center w-full py-4">
              {/* Logo with bottom margin for spacing */}
              <div className="mb-6">
                <Upload className="h-12 w-12 text-emerald-600" />
              </div>

              {/* Main Text */}
              <span className="text-xl font-medium text-emerald-700 mb-2">Upload Payment Proof</span>

              {/* Description with bottom margin for spacing between box layout and bottom text */}
              <span className="text-base text-emerald-600 mb-8">Click to select files or drag and drop</span>

              {/* Footer Info */}
              <div className="flex flex-col items-center gap-1">
                <span className="text-sm text-emerald-500">Accepted formats: PDF, JPG, PNG (Max 5MB per file)</span>
                <span className="text-xs text-emerald-400">You can upload up to 5 images and 1 PDF file only.</span>
              </div>
            </div>
          </Button>
        ) : (
          <div className="space-y-2">
            {selectedFiles.map((file, index) => (
              <Card key={index} className="border-emerald-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {uploadSuccess ? (
                        <CheckCircle className="h-8 w-8 text-green-600" />
                      ) : (
                        <FileText className="h-8 w-8 text-emerald-600" />
                      )}
                      <div>
                        <p className="font-medium text-gray-900">{file.name}</p>
                        <p className="text-sm text-gray-500">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                          {uploadSuccess && (
                            <span className="ml-2 text-green-600 font-medium">✓ Uploaded</span>
                          )}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {!uploading && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => removeFile(index)}
                          disabled={uploading}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Add More Files Button - Hidden if uploading, success, max files reached, or PDF selected */}
            {!uploading && !uploadSuccess && selectedFiles.length < 5 && !selectedFiles.some(f => f.type === 'application/pdf') && (
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="w-full border-emerald-300 text-emerald-600 hover:bg-emerald-50"
              >
                <Upload className="h-4 w-4 mr-2" />
                Add More Files
              </Button>
            )}

            {/* Progress Bar */}
            {uploading && (
              <div className="mt-3">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-emerald-600 h-2 rounded-full transition-all duration-200"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Show ready state when files are selected */}
      {selectedFiles.length > 0 && !uploadSuccess && (
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-3 mb-2">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <h3 className="text-xl font-semibold text-blue-900 mb-1">
              {selectedFiles.length} Payment Proof File{selectedFiles.length > 1 ? 's' : ''} Ready
            </h3>
            <p className="text-blue-700">Click below to upload and send for approval</p>
          </div>

          {/* Send for Approval Button */}
          <Button
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-6 text-lg rounded-lg"
            disabled={uploading}
            onClick={async () => {
              if (onSendForApproval && selectedFiles.length > 0) {
                setUploading(true);
                setUploadProgress(0);

                // Simulate progress
                const progressInterval = setInterval(() => {
                  setUploadProgress((prev) => Math.min(prev + 10, 90));
                }, 200);

                try {
                  await onSendForApproval(selectedFiles);
                  clearInterval(progressInterval);
                  setUploadProgress(100);

                  // Show success toast
                  toast({
                    title: "Upload Successful",
                    description: "Your payment proof has been uploaded and sent for approval.",
                  });

                  // No need to set uploadSuccess or stop uploading state
                  // The redirect will happen in the parent component via onSendForApproval
                } catch (error) {
                  clearInterval(progressInterval);
                  setUploading(false);
                  setUploadProgress(0);

                  // Extract error message
                  const errorMessage = error instanceof Error ? error.message : "Failed to upload payment proof. Please try again.";

                  toast({
                    title: "Upload Failed",
                    description: errorMessage,
                    variant: "destructive",
                  });
                }
              } else {
                toast({
                  title: "Sent for Approval",
                  description: "Your payment proof has been sent to the media owner for review.",
                });
              }
            }}
          >
            {uploading ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2 inline-block" />
                Uploading... {uploadProgress}%
              </>
            ) : (
              `Send ${selectedFiles.length} File${selectedFiles.length > 1 ? 's' : ''} for Approval`
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
